import { ChatMessage as ChatMessageType, GuidedAction } from "../lib/types";
import { ProductCardGroup } from "./ProductCardGroup";
import { ProductCandidatesGrid } from "./ProductCandidatesGrid";
import type { Product } from "../lib/mockData";
import { useState } from "react";
import ReactMarkdown from "react-markdown";
import { mapRecommendedToProduct } from "../lib/utils";
import { RecommendedProduct } from "../lib/types";
// import { ProductCardGroup } from "./ProductCardGroup";
import { useChat } from "../context/ChatContext";
import { Check } from "lucide-react";
import { safeMarkdownComponents } from "../lib/safeMarkdown";

interface ChatMessageProps {
  message: ChatMessageType;
  onProductSelect?: (product: Product) => void;
  onOptionSelect?: (options: Array<{ index: number; label: string }>) => void;
  onActionSelect?: (action: GuidedAction) => void;
  // Anchoring and user action hooks
  userAnchorRef?: (el: HTMLElement | null) => void;
  onUserAction?: () => void;
}

export function ChatMessage({ message, onProductSelect, onOptionSelect, onActionSelect, userAnchorRef, onUserAction }: ChatMessageProps) {
  // Track which options have been selected for this message (if any)
  const [selectedOptionIndices, setSelectedOptionIndices] = useState<number[]>([]);
  const [hasConfirmedSelection, setHasConfirmedSelection] = useState(false);
  // If the message is a result, create Product objects for each recommended product
  const products: Product[] = message.type === "result" && message.recommendation?.recommended_products
    ? message.recommendation.recommended_products.map((rp: RecommendedProduct) => mapRecommendedToProduct(rp, message.search_id))
    : [];
  const candidateProducts: Product[] = message.type === "candidates" && Array.isArray(message.candidates)
    ? message.candidates.map((rp: RecommendedProduct) => mapRecommendedToProduct(rp, message.search_id))
    : [];

  return (
    <div className={`flex ${message.isUser ? 'justify-end' : 'justify-start'} my-4`} ref={(el) => { const node = el as HTMLElement | null; if (message.isUser) userAnchorRef?.(node); }}>
      <div
        className={`
          rounded-2xl text-left
          ${message.isUser ? 'px-[24px] py-[12px] bg-black text-white max-w-[60%]' : 'p-0 max-w-full'}
          ${!message.isUser ? 'animate-in fade-in slide-in-from-top-4' : ''}
        `}
        style={{
          fontFamily: 'var(--font-body)',
          backgroundColor: message.isUser ? 'var(--ww-chat-user-message-bg, var(--primary))' : 'transparent',
          color: message.isUser ? undefined : 'var(--text-secondary)'
        }}
      >
        {message.ui?.variant === 'badge' && !message.isUser ? (
          <div className="inline-flex items-center gap-[8px] px-[8px] py-[4px] rounded-lg text-[16px] font-medium border bg-background text-text-secondary" style={{
            borderColor: 'var(--primary)'
          }}>
            <Check className="w-[16px] h-[16px]" style={{
              color: 'var(--primary)'
            }}/>
            <span>{message.text}</span>
          </div>
        ) : message.isUser ? (
          <div className="whitespace-pre-wrap">{message.text}</div>
        ) : (
          <div className="prose prose-gray max-w-none [&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
            <ReactMarkdown components={safeMarkdownComponents}>
              {message.text}
            </ReactMarkdown>
          </div>
        )}
        {/* Ordered options (category multi-pick) */}
        {message.type === 'ordered_options' && message.options && !message.isUser && (() => {
          const { shoppingList, addPick, removePick, confirmPicks } = useChat();
          const isThisPicking = shoppingList.pickingMessageId === message.id;
          const isLocked = shoppingList.confirmed || !isThisPicking;
          const selected = isThisPicking ? shoppingList.selected : [];
          const canConfirm = isThisPicking && selected.length > 0 && !shoppingList.confirmed;
          return (
            <div className="mt-[8px]">
              <div className="flex flex-wrap gap-[8px]">
                {message.options.map((opt) => {
                  const isPicked = selected.includes(opt.label);
                  const orderIdx = isPicked ? selected.indexOf(opt.label) + 1 : null;
                  return (
                    <label
                      key={opt.index}
                      className="group inline-flex items-center gap-2 px-[16px] py-[10px] rounded-xl border border-gray-200 bg-white text-[16px] cursor-pointer hover:bg-gray-50"
                      style={{
                        fontFamily: 'var(--font-body)',
                        opacity: !isPicked && shoppingList.confirmed ? 0.5 : 1,
                        cursor: isLocked ? 'not-allowed' : 'pointer',
                        borderColor: isPicked ? 'var(--primary)' : undefined,
                      }}
                      title={opt.label}
                    >
                      <input
                        type="checkbox"
                        className="sr-only"
                        checked={isPicked}
                        disabled={isLocked}
                        onChange={(e) => {
                          if (isLocked) return;
                          if (e.target.checked) addPick(opt.label); else removePick(opt.label);
                        }}
                        aria-label={opt.label}
                      />
                      <span
                        className="inline-flex items-center justify-center w-[16px] h-[16px] rounded-md border"
                        style={{
                          borderColor: isPicked ? 'var(--primary)' : '#e5e7eb',
                          backgroundColor: isPicked ? 'var(--color-primary)' : 'white',
                          color: isPicked ? 'white' : 'transparent',
                        }}
                        aria-hidden="true"
                      >
                        {orderIdx}
                      </span>
                      <span>{opt.label}</span>
                    </label>
                  );
                })}
              </div>
              <div className="mt-3">
                <button
                  onClick={() => { if (canConfirm) { onUserAction?.(); confirmPicks(); } }}
                  disabled={!canConfirm}
                  className="px-[16px] py-[10px] rounded-xl border hover:bg-primary/80 bg-primary text-white text-[16px] transition-all"
                  style={{
                    fontFamily: 'var(--font-body)',
                    opacity: canConfirm ? 1 : 0.5,
                    cursor: canConfirm ? 'pointer' : 'not-allowed',
                    backgroundColor: 'var(--color-primary)',
                    borderColor: 'var(--primary)'
                  }}
                >
                  Fortsätt
                </button>
              </div>
            </div>
          );
        })()}

        {/* Regular multi-pick options */}
        {message.type !== 'ordered_options' && message.options && !message.isUser && (
          <div className="mt-[8px]">
            <div className="flex flex-wrap gap-[8px] items-center">
              {message.options.map((opt) => {
                const isSelected = selectedOptionIndices.includes(opt.index);
                return (
                  <button
                    key={opt.index}
                    type="button"
                    onClick={() => {
                      if (hasConfirmedSelection) return;
                      setSelectedOptionIndices((prev) => {
                        if (prev.includes(opt.index)) {
                          return prev.filter((idx) => idx !== opt.index);
                        }
                        return [...prev, opt.index];
                      });
                    }}
                    disabled={hasConfirmedSelection}
                    aria-pressed={isSelected}
                    className="px-[20px] py-[12px] bg-white rounded-xl border border-gray-200 text-[16px] hover:bg-gray-50 transition-all"
                    style={{
                      fontFamily: 'var(--font-body)',
                      borderColor: isSelected ? 'var(--primary)' : undefined,
                      backgroundColor: isSelected ? 'rgba(0, 0, 0, 0.02)' : undefined,
                      opacity: hasConfirmedSelection && !isSelected ? 0.5 : 1,
                    }}
                  >
                    {opt.label}
                  </button>
                );
              })}
              <button
                type="button"
                onClick={() => {
                  if (!message.options || selectedOptionIndices.length === 0 || hasConfirmedSelection) return;
                  setHasConfirmedSelection(true);
                  const selected = message.options.filter((opt) => selectedOptionIndices.includes(opt.index));
                  if (selected.length === 0) return;
                  onUserAction?.();
                  onOptionSelect?.(selected);
                }}
                disabled={selectedOptionIndices.length === 0 || hasConfirmedSelection}
                aria-label="Bekräfta val"
                className="p-[10px] rounded-full flex items-center justify-center hover:opacity-80 transition-all"
                style={{
                  backgroundColor: 'var(--color-primary)',
                  opacity: selectedOptionIndices.length === 0 || hasConfirmedSelection ? 0.5 : 1,
                  cursor: selectedOptionIndices.length === 0 || hasConfirmedSelection ? 'not-allowed' : 'pointer',
                }}
              >
                <Check className="w-[20px] h-[20px]" style={{ color: 'var(--color-background)' }} />
              </button>
            </div>
          </div>
        )}
        {/* Product card group for result messages */}
        {products.length > 0 && (
          <ProductCardGroup products={products} onProductSelect={onProductSelect} />
        )}
        {/* Candidates grid for candidates messages */}
        {message.type === 'candidates' && candidateProducts.length > 0 && (
          <ProductCandidatesGrid products={candidateProducts} onProductSelect={onProductSelect} />
        )}
        {/* Guided action buttons (frontend-generated) */}
        {!message.isUser && Array.isArray(message.actions) && message.actions.length > 0 && (
          <div className="flex flex-wrap gap-[8px] mt-[8px]">
            {message.actions.map((action) => (
                <button
                key={action.id}
                onClick={() => { onUserAction?.(); onActionSelect?.(action); }}
                className="px-[20px] py-[12px] bg-white rounded-xl border border-gray-200 text-[16px] hover:bg-gray-50 transition-all"
                style={{ fontFamily: 'var(--font-body)' }}
              >
                {action.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
} 